import React from 'react';
import './RowControls.css';
import { dispositionColor, useDispositionPalette } from '../dispositionPalette';

/**
 * The controls that live inside an application row — status chip, disposition chip,
 * action buttons.
 *
 * Shared between Initial Portfolio Scoring and Application Portfolio Q&A because both
 * screens list the same applications with the same states, and when the two were styled
 * separately they drifted: one screen's Completed chip was a green pill, the other's was
 * plain text, and the same action was a filled button on one and an outlined one on the
 * other.
 *
 * The visual language, chosen by the owner on 2026-08-02:
 *   · a rectangle with square corners — not the source's pill
 *   · a 1px rule in the SAME colour as the label
 *   · a 4% wash of that colour behind it
 *   · a drawn icon, on currentColor, in place of an emoji
 *   · one height for everything, so a column stays level
 *
 * Metrics live in RowControls.css. Colour is passed inline where it depends on the
 * value (a disposition, an action's meaning); everything else is in the stylesheet.
 */

/**
 * Icons for the row controls.
 *
 * Every one of these replaces an emoji — 📊 🔄 📝 👁️ ✅ 🔒 — and for the same reason the
 * status marker did: an emoji carries its own metrics and its own palette, so it sits off
 * the text's optical centre and stays multicoloured inside a control that is otherwise a
 * single hue. Drawn from one 12px box on `currentColor`, they take the colour of the
 * label beside them.
 */
const ACTION_ICONS: Record<string, JSX.Element> = {
  details: (
    <>
      <rect x="1.5" y="6.5" width="2.4" height="4" rx="0.4" fill="currentColor" />
      <rect x="4.8" y="3.5" width="2.4" height="7" rx="0.4" fill="currentColor" />
      <rect x="8.1" y="1.5" width="2.4" height="9" rx="0.4" fill="currentColor" />
    </>
  ),
  rescore: (
    <>
      <path d="M10.2 6a4.2 4.2 0 1 1-1.3-3" stroke="currentColor" strokeWidth="1.4"
            fill="none" strokeLinecap="round" />
      <path d="M10.6 1.4 L10.6 3.6 L8.4 3.6" stroke="currentColor" strokeWidth="1.4"
            fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  edit: (
    <path d="M8.4 1.8 L10.2 3.6 L4.4 9.4 L2 10 L2.6 7.6 Z" stroke="currentColor"
          strokeWidth="1.3" fill="none" strokeLinejoin="round" />
  ),
  view: (
    <>
      <path d="M0.9 6 C2.6 3 4.2 2.2 6 2.2 C7.8 2.2 9.4 3 11.1 6 C9.4 9 7.8 9.8 6 9.8 C4.2 9.8 2.6 9 0.9 6 Z"
            stroke="currentColor" strokeWidth="1.2" fill="none" />
      <circle cx="6" cy="6" r="1.6" fill="currentColor" />
    </>
  ),
  go: (
    <path d="M3.4 2.4 L8.6 6 L3.4 9.6 Z" fill="currentColor" />
  ),
  done: (
    <path d="M2 6.3 L4.7 9 L10 3" stroke="currentColor" strokeWidth="1.6" fill="none"
          strokeLinecap="round" strokeLinejoin="round" />
  ),
  locked: (
    <>
      <rect x="2.4" y="5.4" width="7.2" height="5.2" rx="0.8" fill="currentColor" />
      <path d="M4.1 5.4 V3.9 a1.9 1.9 0 0 1 3.8 0 V5.4" stroke="currentColor"
            strokeWidth="1.3" fill="none" strokeLinecap="round" />
    </>
  ),
};

export function ActionIcon({ name }: { name: keyof typeof ACTION_ICONS }) {
  return (
    <svg className="md-action-btn__icon" viewBox="0 0 12 12" aria-hidden="true">
      {ACTION_ICONS[name]}
    </svg>
  );
}

/**
 * The inks. There are four, and three of them are rare.
 *
 * `blue` is everything you do to an assessment that already exists — View Details,
 * Q&A Edit, Rescore.
 *
 * `violet` is opening or resuming the questionnaire — Start Q&A, Continue. It is
 * deliberately NOT the amber the In-Process status chip uses: a Continue button sits in
 * the same row as that chip, and sharing one colour makes the two read as the same
 * thing. It is deliberately not the blue either, which would make "go and answer forty
 * questions" look like "have a look at this".
 *
 * Before this the same column mixed a large filled blue button, a small filled orange
 * one and two outlined ones at three different heights, which is what made the column
 * look unsettled beside the status chips.
 */
export const ACTION_INK = {
  /** Looking at an assessment that already exists. */
  blue: '#0d47a1',
  /** Opening or resuming the questionnaire. See the note above. */
  violet: '#4c1d95',
  /** Done, and out of your hands — transferred to Integration. */
  green: '#14532d',
  /** Neutral. */
  grey: '#202124',
} as const;

interface ActionButtonProps {
  /**
   * Marks this as the row's principal action — AT MOST ONE PER ROW.
   *
   * Opt-in, deliberately. The first version derived it from `tone`, which defaulted to
   * 'blue', so every button that did not name a tone came out primary: View Details, Rescore
   * and Q&A Edit all rendered blue side by side, and "at most one per row" — the whole point
   * of the pattern — was true nowhere on the screen. A default that silently promotes
   * everything is worse than no default.
   */
  primary?: boolean;
  tone?: keyof typeof ACTION_INK;
  icon?: keyof typeof ACTION_ICONS;
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * One control for every button in the row, so they cannot end up at different sizes
 * again. Shape and metrics live in ApplicationPortfolioScoring.css under
 * `.md-action-btn`; the tone supplies the single colour that the label, the 1px rule and
 * the icon all take.
 */
/**
 * PATTERN A — the action is neutral; colour belongs to the data.
 *
 * These used to be tinted per `tone`: a blue View Details beside a violet Continue beside a
 * green state, in a row that ALSO carries a colour-coded disposition chip and a colour-coded
 * score. Five colours competing, and the one the reader actually needs — what this
 * application scored and what it should become — had no advantage over the furniture.
 *
 * Owner-directed (2026-08-26) after reviewing four treatments side by side. The rule is that
 * COLOUR MEANS EXACTLY ONE THING on this screen: the disposition. The moment a button is
 * green, green stops reliably meaning Retain.
 *
 * So every action is the same quiet outline, and at most one per row carries the platform
 * blue to mark it as the thing you probably came to do. `tone` survives as `emphasis`
 * because ActionState still needs to distinguish a finished state from a neutral one — a
 * STATE is data, and data may be coloured.
 */
export function ActionButton({ primary = false, icon, label, title, disabled, onClick }: ActionButtonProps) {
  return (
    <button
      className={`md-action-btn${primary ? ' md-action-btn--primary' : ''}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {icon && <ActionIcon name={icon} />}
      <span className="md-action-btn__text">{label}</span>
    </button>
  );
}

/**
 * A read-only state, shown where a button would otherwise be — "Transferred to
 * Integration", "Final: Retire". Same box as ActionButton so the column stays level
 * whether the row offers an action or reports that it no longer can.
 */
export function ActionState({ tone, icon, label }: { tone: keyof typeof ACTION_INK; icon: keyof typeof ACTION_ICONS; label: string }) {
  const ink = ACTION_INK[tone];
  return (
    <span className="md-action-btn md-action-btn--static"
          style={{ color: ink, borderColor: ink, backgroundColor: `${ink}24` }}>
      <ActionIcon name={icon} />
      <span className="md-action-btn__text">{label}</span>
    </span>
  );
}

/**
 * The disposition ink.
 *
 * `getRecommendationColor` returns the 500-level hue the source used as a solid FILL
 * behind white text. Those values are too light to be text or a 1px rule on white, so
 * each moves one step down its own ramp to the 700 level — same hue, enough weight to
 * read. The fill values are untouched and still drive the score bar.
 */
/**
 * @deprecated Superseded by `dispositionColor()` / `useDispositionPalette()` in
 * `pages/apr/dispositionPalette.ts`, which reads the Disposition Matrix instead of hardcoding
 * a palette. Kept only so an import that has not been migrated still compiles; it now
 * DELEGATES rather than carrying its own values, so it cannot drift from the matrix.
 */
export function dispositionInk(recommendation: string): string {
  return dispositionColor(recommendation).ink;
}

/** The disposition chip — the status chip's box, in the disposition's own colour. */
/**
 * The disposition chip — FILLED with the Disposition Matrix's own colour.
 *
 * It used to be a tinted outline in a DERIVED ink, which meant Re-Design rendered a brownish
 * #8c6a04 while the matrix stored #ffc107, and an administrator editing that colour picker saw
 * nothing like it on screen. Owner-directed twice: the colours here must be the matrix's.
 *
 * Those values were chosen as FILLS — that is why three of the four are unreadable as text on
 * white — so using them as fills is both what was asked for and the only way they work. The
 * label takes black or white by luminance, computed rather than picked, so a future edit to the
 * matrix cannot produce an unreadable chip.
 */
export function DispositionChip({ value }: { value: string }) {
  const palette = useDispositionPalette();
  const { base, onBase } = palette(value);
  return (
    <div
      className="md-chip md-chip--disposition"
      style={{ backgroundColor: base, color: onBase }}
    >
      <span className="md-chip__dot" />
      {value}
    </div>
  );
}

/**
 * The assessment status chip.
 *
 * Both the Tree and the Flat view render this, and they rendered it twice over with
 * the same three-way emoji ternary inline. One component so the two views cannot drift
 * apart, and so the marker is defined once.
 *
 * The marker is an inline SVG on `currentColor` rather than the emoji it replaces:
 * ✅ ⏳ ⭕ each carry their own metrics and their own palette, so they sat off the
 * text's optical centre and stayed the same colour whatever the chip did. A tick, a
 * quarter-filled ring and an empty ring, all drawn from one 9px box, sit on the type
 * and take its colour.
 *
 * Sizing lives in ApplicationPortfolioScoring.css under `.md-chip--status`.
 */
export function AssessmentStatusChip({ status }: { status: string }) {
  const modifier = status.toLowerCase().replace(/\s+/g, '-');

  // "Complete" AND "Completed". The two screens that share this chip disagree on the
  // word — Initial Portfolio Scoring stores 'Completed', Application Portfolio Q&A
  // derives 'Complete' — and matching only the longer one silently gave a finished
  // application the EMPTY ring, the same marker as one nobody has touched. The
  // stylesheet already carried both spellings; only this test did not.
  const isComplete = status === 'Complete' || status === 'Completed';

  const marker =
    isComplete ? (
      <svg className="md-chip__marker" viewBox="0 0 10 10" fill="none" stroke="currentColor"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M1.5 5.3 L3.9 7.7 L8.5 2.3" />
      </svg>
    ) : status === 'In-Process' ? (
      <svg className="md-chip__marker" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <circle cx="5" cy="5" r="4.1" stroke="currentColor" strokeWidth="1.6" />
        <path d="M5 5 L5 1.4 A3.6 3.6 0 0 1 8.1 6.8 Z" fill="currentColor" />
      </svg>
    ) : (
      <svg className="md-chip__marker" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <circle cx="5" cy="5" r="4.1" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );

  return (
    <div className={`md-chip md-chip--status md-chip--${modifier}`}>
      {marker}
      <span className="md-chip__text">{status}</span>
    </div>
  );
}
